import React, { useEffect } from "react";
import {
  makeStyles,
  withStyles,
  Paper,
  Grid,
  Typography,
  Box,
  LinearProgress,
  Chip,
} from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import DataUsageIcon from "@material-ui/icons/DataUsage";
import { alpha } from "@mui/material";
import { fetchConsumptionData } from "administration/redux/actions";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(4),
    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
    width: "80%",
    margin: "auto",
  },
  header: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  iconBox: {
    backgroundColor: theme.palette.action.hover,
    padding: theme.spacing(1),
    borderRadius: "50%",
    marginRight: theme.spacing(2),
    display: "flex",
  },
  title: {
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  heroNumber: {
    fontWeight: 500,
    fontSize: "2rem",
    lineHeight: 1,
    marginBottom: theme.spacing(1),
  },
  subText: {
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  usageText: {
    marginTop: theme.spacing(1.5),
    fontWeight: 600,
  },
  alert: {
    marginTop: theme.spacing(3),
    borderRadius: 8,
  },
}));

// Dynamic Progress Bar Component
const CustomLinearProgress = withStyles((theme) => ({
  root: {
    height: 24,
    borderRadius: 2,
  },
  colorPrimary: {
    backgroundColor: theme.palette.grey[200],
  },
  bar: {
    borderRadius: 2,
  },
}))(LinearProgress);

export const Consumption = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const consumption = useSelector(
    ({ requests }) =>
      requests.queries.FETCH_CONSUMPTION_DATA && requests.queries.FETCH_CONSUMPTION_DATA.data
  );
  const totalTokens = consumption?.nbTokens || 0;
  const consumedTokens = consumption?.nbConsumedTokens || 0;

  const percentage = totalTokens > 0 ? (consumedTokens / totalTokens) * 100 : 0;

  let statusColor = "#4caf50ff";
  let statusLabel = t("Healthy");

  if (percentage >= 80 && percentage < 100) {
    statusColor = "#ff9800";
    statusLabel = t("Warning");
  } else if (percentage >= 100) {
    statusColor = "#f44336";
    statusLabel = t("Limit Exceeded");
  }

  useEffect(() => {
    dispatch(fetchConsumptionData());
  }, [dispatch]);

  return (
    <Paper className={classes.root}>
      <div className={classes.header}>
        <div className={classes.iconBox}>
          <DataUsageIcon color="action" />
        </div>

        <Typography variant="h6" color="primary">
          {t("Consumption_statistics")}
        </Typography>

        <Box flexGrow={1} />

        <Chip
          label={statusLabel}
          style={{
            backgroundColor: alpha(statusColor, 0.15),
            color: statusColor,
            fontWeight: "bold"
          }}
        />
      </div>

      <Grid container spacing={4} alignItems="center">
        <Grid item xs={12} md={4}>
          <Typography
            className={classes.heroNumber}
            style={{ color: statusColor }}
          >
            {percentage.toFixed(0)}%
          </Typography>

          <Typography variant="body2" color="textSecondary">
            {t("of_Total_Tokens_Used")}
          </Typography>
        </Grid>

        <Grid item xs={12} md={8}>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="body2" color="primary">
              0 {t("Tokens")}
            </Typography>
            <Typography variant="body2" color="primary">
              {totalTokens.toLocaleString()} {t("Tokens")}
            </Typography>
          </Box>

          <CustomLinearProgress
            variant="determinate"
            value={Math.min(percentage, 100)}
          />

          {/* Dynamic bar color override */}
          <style>{`
            .MuiLinearProgress-barColorPrimary {
              background-color: ${statusColor} !important;
            }
          `}</style>

          <Typography
            variant="body2"
            color="textSecondary"
            align="center"
            className={classes.usageText}
          >
            {consumedTokens.toLocaleString()} / {totalTokens.toLocaleString()} {t("Tokens")}
          </Typography>
        </Grid>
      </Grid>

    </Paper>
  );
};

export default Consumption;